type ToastPosition =
	| "bottom-center"
	| "bottom-left"
	| "bottom-right"
	| "top-center"
	| "top-left"
	| "top-right";

type ToastVariant = "info" | "success" | "warning" | "error";

type ToastOptions = {
	message: string;
	actionText?: string;
	duration?: number;
	onAction?: () => void;
	position?: ToastPosition;
	variant?: ToastVariant;
};

const toastContainers: Record<string, HTMLElement> = {};

function getToastContainer(position: ToastPosition): HTMLElement {
	if (toastContainers[position]) {
		return toastContainers[position];
	}

	const el = document.createElement("div");
	el.className = `toast-container ${position}`;
	document.body.appendChild(el);
	toastContainers[position] = el;
	return el;
}

export function showToast(opts: ToastOptions): void {
	const position = opts.position ?? "bottom-center";
	const variant = opts.variant ?? "info";
	const container = getToastContainer(position);

	const toast = document.createElement("div");
	toast.className = `toast toast-${variant}`;

	const message = document.createElement("div");
	message.className = "toast-message";
	message.textContent = opts.message;
	toast.appendChild(message);

	if (opts.actionText && opts.onAction) {
		const btn = document.createElement("button");
		btn.className = "toast-action";
		btn.type = "button";
		btn.textContent = opts.actionText;
		btn.onclick = () => {
			opts.onAction?.();
			removeToast(toast);
		};
		toast.appendChild(btn);
	}

	container.appendChild(toast);

	requestAnimationFrame(() => {
		toast.classList.add("show");
	});

	const duration = opts.duration ?? (opts.actionText ? 6000 : 4000);

	let timeout = window.setTimeout(() => {
		removeToast(toast);
	}, duration);

	toast.onmouseenter = () => {
		window.clearTimeout(timeout);
	};

	toast.onmouseleave = () => {
		timeout = window.setTimeout(() => {
			removeToast(toast);
		}, 2000);
	};

	enableSwipeToDismiss(toast, () => {
		window.clearTimeout(timeout);
		removeToast(toast);
	});
}

function removeToast(toast: HTMLElement): void {
	if (!toast.isConnected) {
		return;
	}

	toast.classList.remove("show");
	toast.classList.add("removing");

	window.setTimeout(() => {
		toast.remove();
	}, 200);
}

function enableSwipeToDismiss(toast: HTMLElement, onDismiss: () => void): void {
	let startX = 0;
	let startY = 0;
	let currentX = 0;
	let currentY = 0;
	let isPointerDown = false;
	let isSwiping = false;

	const swipeThreshold = 80;
	const lockThreshold = 12;

	const reset = () => {
		isPointerDown = false;
		isSwiping = false;
		startX = 0;
		startY = 0;
		currentX = 0;
		currentY = 0;
		toast.style.transform = "";
		toast.style.opacity = "";
	};

	const onPointerDown = (ev: PointerEvent) => {
		if (ev.pointerType !== "touch") {
			return;
		}

		isPointerDown = true;
		isSwiping = false;
		startX = ev.clientX;
		startY = ev.clientY;
		currentX = ev.clientX;
		currentY = ev.clientY;
		toast.style.transition = "none";
	};

	const onPointerMove = (ev: PointerEvent) => {
		if (!isPointerDown) {
			return;
		}

		currentX = ev.clientX;
		currentY = ev.clientY;

		const deltaX = currentX - startX;
		const deltaY = currentY - startY;

		if (!isSwiping) {
			if (Math.abs(deltaX) < lockThreshold) {
				return;
			}

			if (Math.abs(deltaY) > Math.abs(deltaX)) {
				reset();
				return;
			}

			isSwiping = true;
		}

		const opacity = Math.max(0.35, 1 - Math.abs(deltaX) / 160);
		toast.style.transform = `translateX(${deltaX}px)`;
		toast.style.opacity = opacity.toString();
	};

	const onPointerUp = () => {
		if (!isPointerDown) {
			return;
		}

		const deltaX = currentX - startX;

		toast.style.transition = "";
		toast.style.opacity = "";

		if (isSwiping && Math.abs(deltaX) >= swipeThreshold) {
			toast.style.transform = `translateX(${deltaX > 0 ? 140 : -140}px)`;
			toast.style.opacity = "0";
			window.setTimeout(() => {
				onDismiss();
			}, 150);
			return;
		}

		toast.style.transform = "";
		reset();
	};

	const onPointerCancel = () => {
		toast.style.transition = "";
		toast.style.transform = "";
		toast.style.opacity = "";
		reset();
	};

	toast.addEventListener("pointerdown", onPointerDown, { passive: true });
	toast.addEventListener("pointermove", onPointerMove, { passive: true });
	toast.addEventListener("pointerup", onPointerUp);
	toast.addEventListener("pointercancel", onPointerCancel);
}